import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Building2, Save, UserCheck, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormActions, FormField, FormPageHeader, FormSection } from '@/components/common/FormSection'
import { AdditionalInfoSection, ContactsSection, PrimaryEmailSection, WhatsAppSection } from '@/components/clients/ClientFormSections'
import { cn } from '@/utils/cn'
import { clientService } from '../services/client-service'
import type { Contact, CreateClientDto, PersonalData, CompanyData } from '../types/client'

import { getErrorMessage } from '../utils/error.utils'
interface CreateClientFormState {
  clientType: 'PERSON' | 'COMPANY'
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'PROSPECT'
  whatsappPhone: string
  tags: string[]
  notes: string
  personalData: {
    firstName: string
    lastName: string
    dateOfBirth: string
    cpf: string
    rg: string
    nationality: string
    profession: string
  }
  companyData: {
    tradeName: string
    companyName: string
    cnpj: string
    stateRegistration: string
    municipalRegistration: string
    foundedDate: string
    activitySector: string
    legalRepresentative: string
  }
  contacts: Omit<Contact, 'id' | 'clientId' | 'createdAt'>[]
}

export function CreateClientPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clientType, setClientType] = useState<'PERSON' | 'COMPANY'>('PERSON')

  const [formData, setFormData] = useState<CreateClientFormState>({
    clientType: 'PERSON',
    status: 'ACTIVE',
    whatsappPhone: '',
    tags: [],
    notes: '',
    personalData: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      cpf: '',
      rg: '',
      nationality: '',
      profession: ''
    },
    companyData: {
      tradeName: '',
      companyName: '',
      cnpj: '',
      stateRegistration: '',
      municipalRegistration: '',
      foundedDate: '',
      activitySector: '',
      legalRepresentative: ''
    },
    contacts: []
  })

  const [primaryEmail, setPrimaryEmail] = useState('')

  const handleInputChange = (field: string, value: unknown, section?: 'personalData' | 'companyData') => {
    if (section) {
      setFormData({
        ...formData,
        [section]: {
          ...formData[section],
          [field]: value
        }
      })
    } else {
      setFormData({
        ...formData,
        [field]: value
      })
    }
  }

  const handleClientTypeChange = (newType: 'PERSON' | 'COMPANY') => {
    setClientType(newType)
    setFormData({
      ...formData,
      clientType: newType
    })
  }

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [
        ...(formData.contacts || []),
        {
          contactType: 'WHATSAPP',
          label: '',
          value: '',
          isPrimary: false,
          isVerified: false,
          notes: ''
        }
      ]
    })
  }

  const updateContact = (index: number, field: string, value: unknown) => {
    if (!formData.contacts) return
    
    const updatedContacts = [...formData.contacts]
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    }
    
    setFormData({
      ...formData,
      contacts: updatedContacts
    })
  }

  const removeContact = (index: number) => {
    if (!formData.contacts) return
    
    const updatedContacts = formData.contacts.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      contacts: updatedContacts
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const stripEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> | undefined => {
        const cleaned: Partial<T> = {}
        for (const key of Object.keys(obj) as (keyof T)[]) {
          const value = obj[key]
          if (value !== '' && value !== null && value !== undefined) {
            cleaned[key] = value
          }
        }
        return Object.keys(cleaned).length > 0 ? cleaned : undefined
      }

      const cleanedContacts: NonNullable<CreateClientDto['contacts']> = (formData.contacts || [])
        .filter(contact => contact.value.trim())
        .map(contact => ({
          contactType: contact.contactType,
          value: contact.value,
          isPrimary: contact.isPrimary,
          isVerified: contact.isVerified,
          ...(contact.label?.trim() ? { label: contact.label } : {}),
          ...(contact.notes?.trim() ? { notes: contact.notes } : {})
        }))

      if (primaryEmail && primaryEmail.trim()) {
        cleanedContacts.unshift({
          contactType: 'EMAIL',
          value: primaryEmail.trim(),
          isPrimary: true,
          isVerified: false
        })
      }

      const submitData: CreateClientDto = {
        clientType: formData.clientType,
        status: formData.status,
        tags: formData.tags || [],
        contacts: cleanedContacts
      }

      if (formData.whatsappPhone && formData.whatsappPhone.trim()) {
        submitData.whatsappPhone = formData.whatsappPhone.trim()
      }

      if (formData.notes && formData.notes.trim()) {
        submitData.notes = formData.notes.trim()
      }

      if (formData.clientType === 'PERSON') {
        const personalData = formData.personalData
        if (personalData) {
          const cleanedPersonalData = stripEmpty<Omit<PersonalData, 'clientId'>>({
            fullName: `${personalData.firstName || ''} ${personalData.lastName || ''}`.trim(),
            firstName: personalData.firstName,
            lastName: personalData.lastName,
            cpf: personalData.cpf?.replace(/\D/g, ''),
            rg: personalData.rg,
            birthDate: personalData.dateOfBirth,
            nationality: personalData.nationality,
            profession: personalData.profession
          })

          if (cleanedPersonalData?.fullName) {
            submitData.personalData = cleanedPersonalData as Omit<PersonalData, 'clientId'>
          }
        }
      } else {
        const companyData = formData.companyData
        if (companyData) {
          const cleanedCompanyData = stripEmpty<Omit<CompanyData, 'clientId'>>({
            companyName: companyData.companyName,
            tradingName: companyData.tradeName,
            tradeName: companyData.tradeName,
            cnpj: companyData.cnpj?.replace(/\D/g, ''),
            stateRegistration: companyData.stateRegistration,
            municipalRegistration: companyData.municipalRegistration,
            foundedDate: companyData.foundedDate,
            activitySector: companyData.activitySector,
            legalRepresentative: companyData.legalRepresentative
          })

          if (cleanedCompanyData?.companyName) {
            submitData.companyData = cleanedCompanyData as Omit<CompanyData, 'clientId'>
          }
        }
      }

      await clientService.createClient(submitData)
      navigate('/clients')
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar contato'))
    } finally {
      setLoading(false)
    }
  }

  const typeOption = (value: 'PERSON' | 'COMPANY', Icon: typeof UserCheck, title: string, subtitle: string) => (
    <label
      className={cn(
        'relative flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/50',
        clientType === value ? 'border-primary bg-primary/5' : 'hover:bg-muted/60'
      )}
    >
      <input
        type="radio"
        name="clientType"
        value={value}
        checked={clientType === value}
        onChange={(e) => handleClientTypeChange(e.target.value as 'PERSON' | 'COMPANY')}
        className="sr-only"
      />
      <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </label>
  )

  const textField = (
    id: string,
    label: string,
    field: string,
    section: 'personalData' | 'companyData',
    options: { type?: string; required?: boolean; placeholder?: string } = {}
  ) => {
    const data = formData[section] as Record<string, string>
    return (
      <FormField label={label} htmlFor={id}>
        <Input
          id={id}
          type={options.type ?? 'text'}
          required={options.required}
          placeholder={options.placeholder}
          value={data?.[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value, section)}
        />
      </FormField>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <FormPageHeader
        title="Novo Contato"
        description="Cadastre um novo contato no sistema"
        icon={Users}
        backTo="/clients"
        backLabel="Voltar"
      />

      <form onSubmit={handleSubmit} className="flex max-w-4xl flex-col gap-4">
        <FormSection title="Tipo de Contato" icon={Users} className="md:grid-cols-2">
          {typeOption('PERSON', UserCheck, 'Pessoa Física', 'Contato individual')}
          {typeOption('COMPANY', Building2, 'Pessoa Jurídica', 'Empresa ou organização')}
        </FormSection>

        {clientType === 'PERSON' ? (
          <FormSection title="Dados Pessoais" icon={UserCheck} className="md:grid-cols-2">
            {textField('client-first-name', 'Nome *', 'firstName', 'personalData', { required: true })}
            {textField('client-last-name', 'Sobrenome *', 'lastName', 'personalData', { required: true })}
            {textField('client-cpf', 'CPF', 'cpf', 'personalData', { placeholder: '000.000.000-00' })}
            {textField('client-rg', 'RG', 'rg', 'personalData')}
            {textField('client-birth-date', 'Data de Nascimento', 'dateOfBirth', 'personalData', { type: 'date' })}
            {textField('client-profession', 'Profissão', 'profession', 'personalData')}
          </FormSection>
        ) : (
          <FormSection title="Dados da Empresa" icon={Building2} className="md:grid-cols-2">
            {textField('company-trade-name', 'Nome Fantasia *', 'tradeName', 'companyData', { required: true })}
            {textField('company-name', 'Razão Social *', 'companyName', 'companyData', { required: true })}
            {textField('company-cnpj', 'CNPJ', 'cnpj', 'companyData', { placeholder: '00.000.000/0001-00' })}
            {textField('company-activity-sector', 'Setor de Atividade', 'activitySector', 'companyData')}
            {textField('company-founded-date', 'Data de Fundação', 'foundedDate', 'companyData', { type: 'date' })}
            {textField('company-legal-representative', 'Representante Legal', 'legalRepresentative', 'companyData')}
          </FormSection>
        )}

        <WhatsAppSection id="client-whatsapp" value={formData.whatsappPhone || ''} onChange={(value) => handleInputChange('whatsappPhone', value)} />
        <PrimaryEmailSection id="client-primary-email" value={primaryEmail} onChange={setPrimaryEmail} />
        <ContactsSection contacts={formData.contacts || []} onAdd={addContact} onUpdate={updateContact} onRemove={removeContact} />
        <AdditionalInfoSection
          statusId="client-status"
          notesId="client-notes"
          status={formData.status}
          notes={formData.notes || ''}
          onStatusChange={(status) => handleInputChange('status', status)}
          onNotesChange={(notes) => handleInputChange('notes', notes)}
        />

        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
        )}

        <FormActions>
          <Button variant="outline" asChild><Link to="/clients">Cancelar</Link></Button>
          <Button type="submit" disabled={loading} loading={loading}>
            {!loading && <Save className="size-4" />}
            Salvar Contato
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
